import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BiChevronDown } from 'react-icons/bi';
import { Menu, Transition } from '@headlessui/react';

import { Checkbox } from '@client/toolkit';

import { useBuildingsTabQueryParams } from '../hooks/use-buildings-query-params';

type ActiveBuildingType = 'Office' | 'RetailHighStreet' | 'Retail' | 'Logistics' | 'Industrial' | 'Hotel' | 'Healthcare' | 'Education' | 'Mixed';
const BUILDING_TYPE_OPTIONS: ActiveBuildingType[] = ['Office', 'RetailHighStreet', 'Retail', 'Logistics', 'Industrial', 'Hotel', 'Healthcare', 'Education', 'Mixed'];

export function BuildingsTab() {
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const [queryParams, setQueryParam] = useBuildingsTabQueryParams();
  const selectedTypes: ActiveBuildingType[] = queryParams.buildingTypes ?? [];

  const hasSelection = useMemo(() => selectedTypes.length > 0, [selectedTypes.length]);

  const handleToggleType = (type: ActiveBuildingType) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    setQueryParam({ buildingTypes: newTypes.length > 0 ? newTypes : undefined });
  };

  const handleSelectAll = () => {
    setQueryParam({ buildingTypes: undefined });
  };

  const buttonLabel = useMemo(() => {
    if (!hasSelection) return tCommon('all');
    if (selectedTypes.length === 1) return tType(`BuildingType.${selectedTypes[0]}`);
    return `${selectedTypes.length} valgt`;
  }, [hasSelection, selectedTypes, tCommon, tType]);

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="inline-flex items-center justify-between bg-[#322F2C] hover:enabled:bg-[#322F2C]/80 pl-3 pr-3 py-2 rounded text-white text-sm min-w-[200px]">
        <div className="flex flex-col items-start mr-2">
          <span className="text-[10px] text-[#f5bc6f] uppercase tracking-wide">Bygningstype</span>
          <span className="text-sm">{buttonLabel}</span>
        </div>
        <BiChevronDown className="w-4 h-4 text-brand-primary" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Menu.Items className="absolute left-0 mt-1 min-w-[200px] w-full bg-[#201E1A] rounded shadow-lg focus:outline-none z-50">
          <div className="py-1">
            <Menu.Item>
              {() => (
                <button
                  className="w-full text-left px-4 py-3 text-sm text-white hover:bg-[#272521] border-b border-[#322F2C]"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelectAll();
                  }}
                >
                  <Checkbox checked={!hasSelection} readOnly className="mr-2">
                    <span className="ml-2">{tCommon('all')}</span>
                  </Checkbox>
                </button>
              )}
            </Menu.Item>

            {BUILDING_TYPE_OPTIONS.map((option, index) => (
              <Menu.Item key={option}>
                {() => (
                  <button
                    className={`w-full text-left px-4 py-3 text-sm text-white hover:bg-[#272521] ${index < BUILDING_TYPE_OPTIONS.length - 1 ? 'border-b border-[#322F2C]' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleType(option);
                    }}
                  >
                    <Checkbox checked={selectedTypes.includes(option)} readOnly className="mr-2">
                      <span className="ml-2">{tType(`BuildingType.${option}`)}</span>
                    </Checkbox>
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
